import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import StateBlock from '../ui/StateBlock';
import { useAuthStore } from '../../store/authStore';
import { closedPathTarget, internalOnlyOffNavClosed, isPortalIdentity, portalOffNavClosed, screenForPath } from './screenAccess';

/**
 * The one route guard for every screen inside the shell (owner decision
 * 2026-10-07, decision 1). The path is mapped to its screen
 * (`screenAccess.canOpenPath`): a screen the person may not open does not
 * exist for them, so its address goes home silently (replace, no message).
 * It replaces the per-route AdminRoute / ManagerRoute / AgentRoute /
 * InternalRoute and the "אין הרשאה" page: the role rules they held are the
 * defaults of the screen permissions. The server still refuses the data
 * (403); this guard only keeps the UI honest.
 *
 * The portal identity (customer_viewer) has no internal screen: a closed
 * address takes it to `/portal`, and a ticket / conversation address to the
 * portal's requests tab (`closedPathTarget`).
 *
 * Until the permissions are known the page waits (a skeleton), so a blocked
 * screen never flashes and an allowed one never bounces home.
 */
export default function ScreenGate() {
  const { ready, canPath, access } = usePermissions();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  // DQ-018: a customer identity (known from the login itself) never opens an internal off-nav page.
  if (isPortalIdentity(user) && portalOffNavClosed(location.pathname)) {
    return <Navigate to={closedPathTarget('customer_viewer', location.pathname, true)} replace />;
  }
  // Home, profile, the calendar: no screen to check, no wait. (FIX-B15: the
  // off-nav pages an internal-only employee never opens wait for the answer.)
  if (!screenForPath(location.pathname) && !internalOnlyOffNavClosed(location.pathname)) return <Outlet />;
  if (!ready) return <StateBlock kind="loading" size="page" rows={4} />;
  if (!canPath(location.pathname)) {
    return <Navigate to={closedPathTarget(access.role, location.pathname + location.search, access.portalOnly, access.internalOnly)} replace />;
  }
  return <Outlet />;
}
